/**
 * Task 110B — Social Connections Card (UI scaffolding, read-only).
 *
 * Read-only display of a client's connected social accounts per
 * platform. The card is intentionally **not wired into the router /
 * navigation** in this task — it ships as a standalone component
 * that the future PublishingPage / ClientPortal page can drop in
 * with one line:
 *
 *   <SocialConnectionsCard
 *     viewerRole="agency" | "client"
 *     connections={connectionsForThisClient}
 *     onConnectClick={() => { ... }}  // currently disabled
 *   />
 *
 * Hard rules (enforced by 110B test pin):
 *   - Arabic-only visible UI. Latin platform names ("Instagram",
 *     "TikTok") never render — full-Arabic labels only.
 *   - Token / secret fields are NEVER rendered. The component does
 *     not even accept them as props — its `SocialConnectionRow`
 *     prop shape comes from the existing FE type which already
 *     omits token columns.
 *   - For agency viewers: read-only status. No "ربط الحساب" /
 *     "إعادة الربط" / "إلغاء الربط" buttons. Agency must not
 *     impersonate the client to connect their accounts.
 *   - For client viewers: action buttons render but are DISABLED
 *     with placeholder copy. Real OAuth scaffold is a future task
 *     (see docs/110B-social-connections-readiness/README.md). The
 *     `onConnectClick` callback is only invoked when OAuth lands;
 *     for now it's never called because every button is disabled.
 *   - V1 platforms only (instagram / tiktok / snapchat). Facebook
 *     is in the FE enum but the card hides it (out of automated
 *     publishing scope; see 110A architecture audit).
 */

import { useEffect, useRef, useState } from 'react';
import type { SocialConnectionRow, SocialConnectionStatus } from '../../api/types.js';

/**
 * Three platforms the card displays a row for. Order matters — it's
 * the order the client sees them in.
 */
const PLATFORMS_DISPLAY_ORDER = ['instagram', 'tiktok', 'snapchat'] as const;
type DisplayablePlatform = (typeof PLATFORMS_DISPLAY_ORDER)[number];

// Task 112C / 113B — per-platform OAuth status. Three states:
//   * 'live'     — `start-` AND `complete-social-connection-oauth` are
//                  fully implemented; the connect button enables and
//                  fires the live OAuth flow.
//   * 'scaffold' — `start-social-connection-oauth` mints a real
//                  authorize URL, but `complete-social-connection-oauth`
//                  returns 501 `not_implemented_yet`. The connect
//                  button stays disabled with a "قيد التحضير" hint
//                  so the operator sees the row but can't start a
//                  flow that won't complete (TikTok today).
//   * 'manual'   — no public API for organic publishing. The button
//                  is disabled with the "النشر يدوي" hint (Snapchat
//                  per `docs/110A-publishing-foundation/platforms/snapchat.md`).
type PlatformOAuthStatus = 'live' | 'scaffold' | 'manual';

const PLATFORM_OAUTH_STATUS: Record<DisplayablePlatform, PlatformOAuthStatus> = {
  instagram: 'live',
  // Task 187 — TikTok promoted from `scaffold` to `live`. Task 186
  // (PR #177, merged at f60cc63) replaced the 501 stub in
  // `complete-social-connection-oauth` with the real TikTok
  // authorization_code exchange + V2 token-lifecycle persistence;
  // `start-social-connection-oauth` already minted the TikTok
  // authorize URL since Task 113-era work. With the operator-set
  // TIKTOK_CLIENT_KEY / TIKTOK_CLIENT_SECRET / TIKTOK_REDIRECT_URI
  // secrets in place, the round-trip completes. CONNECT only — TikTok
  // PUBLISHING stays gated separately (the resolver + Worker adapter
  // are still TikTok-blind per tasks/185 T2/T3a).
  tiktok: 'live',
  // Snapchat — no public organic-publishing API for non-partners.
  // Stays manual indefinitely until that changes upstream.
  snapchat: 'manual',
};

function platformOAuthStatus(p: DisplayablePlatform): PlatformOAuthStatus {
  return PLATFORM_OAUTH_STATUS[p];
}

/**
 * Full Arabic labels per platform. NO Latin prefixes leak into the
 * UI — mirror of `inboxPlatformLabelAr` (107E) / `productionTaskPlatformLabelAr`
 * (108B) for consistency across surfaces.
 */
const PLATFORM_LABELS_AR: Record<DisplayablePlatform, string> = {
  instagram: 'إنستجرام',
  tiktok: 'تيك توك',
  snapchat: 'سناب شات',
};

/**
 * One-line Arabic description per platform — explains why this
 * connection exists in the card (publishing pipeline). Helps the
 * client understand what they're connecting and why.
 */
const PLATFORM_DESCRIPTIONS_AR: Record<DisplayablePlatform, string> = {
  instagram: 'لنشر المنشورات والريلز والستوري على حساب إنستجرام للأعمال.',
  tiktok: 'لنشر مقاطع الفيديو على حساب تيك توك للمبدعين.',
  snapchat: 'لنشر القصص على حساب سناب شات (نشر يدوي مؤقتاً في النسخة الأولى).',
};

/**
 * The 5 client-facing statuses (per the spec). Maps from the wire
 * `SocialConnectionStatus` PLUS the synthetic `not_connected` state
 * for platforms with NO connection row at all.
 */
type ClientFacingStatus =
  | 'not_connected' // no row exists
  | 'connected' // status='active'
  | 'reconnect_required' // status='reauthorization_required'
  | 'expired' // status='disabled' (we treat this as "expired/admin-disabled")
  | 'disconnected'; // status='revoked'

const CLIENT_STATUS_LABELS_AR: Record<ClientFacingStatus, string> = {
  not_connected: 'غير مربوط',
  connected: 'مربوط',
  reconnect_required: 'يحتاج إعادة ربط',
  expired: 'منتهي الصلاحية',
  disconnected: 'مفصول',
};

/**
 * Map the wire status to the client-facing status. Pure function —
 * the test pin asserts the mapping is exhaustive.
 */
function clientStatusFromWire(status: SocialConnectionStatus | null): ClientFacingStatus {
  if (status === null) return 'not_connected';
  switch (status) {
    case 'active':
      return 'connected';
    case 'reauthorization_required':
      return 'reconnect_required';
    case 'disabled':
      return 'expired';
    case 'revoked':
      return 'disconnected';
  }
}

/**
 * Status badge tone class. Mirrors the pill-* convention used
 * throughout the page (104C / 107E).
 */
function statusBadgeTone(status: ClientFacingStatus): string {
  switch (status) {
    case 'connected':
      return 'pill pill-active';
    case 'not_connected':
      return 'pill pill-draft';
    case 'reconnect_required':
      return 'pill pill-review';
    case 'expired':
      return 'pill pill-archived';
    case 'disconnected':
      return 'pill pill-error';
  }
}

/**
 * For agency viewers: a derived "can the runner auto-publish to this
 * connection right now?" hint. Based purely on the wire status — no
 * token-validity check (the runner does that at call time). Pure.
 */
function canAutoPublishHint(status: ClientFacingStatus): {
  readonly label: string;
  readonly tone: 'ok' | 'warn' | 'no';
} {
  switch (status) {
    case 'connected':
      return { label: 'النشر التلقائي متاح', tone: 'ok' };
    case 'reconnect_required':
      return { label: 'النشر التلقائي معطّل — يحتاج إعادة ربط', tone: 'warn' };
    case 'expired':
      return { label: 'النشر التلقائي معطّل — انتهت الصلاحية', tone: 'warn' };
    case 'disconnected':
      return { label: 'النشر التلقائي غير متاح — العميل فصل الحساب', tone: 'no' };
    case 'not_connected':
      return { label: 'لم يتم الربط بعد', tone: 'no' };
  }
}

interface PerPlatformRow {
  readonly platform: DisplayablePlatform;
  readonly connection: SocialConnectionRow | null;
}

/**
 * Pick at most one active (non-archived) connection per platform.
 * The DB enforces uniqueness via the partial unique index already
 * (`social_connections_active_identity_uidx`), but the FE picks the
 * most-recent active row defensively so a soft-archive lag never
 * shows two rows for the same platform.
 */
function pickActivePerPlatform(
  connections: ReadonlyArray<SocialConnectionRow>,
): ReadonlyArray<PerPlatformRow> {
  const byPlatform = new Map<DisplayablePlatform, SocialConnectionRow>();
  for (const c of connections) {
    if (c.archived_at !== null) continue;
    if (!PLATFORMS_DISPLAY_ORDER.includes(c.platform as DisplayablePlatform)) continue;
    const platform = c.platform as DisplayablePlatform;
    const existing = byPlatform.get(platform);
    if (!existing || c.updated_at > existing.updated_at) {
      byPlatform.set(platform, c);
    }
  }
  return PLATFORMS_DISPLAY_ORDER.map((platform) => ({
    platform,
    connection: byPlatform.get(platform) ?? null,
  }));
}

export interface SocialConnectionsCardProps {
  readonly viewerRole: 'client' | 'agency';
  readonly connections: ReadonlyArray<SocialConnectionRow>;
  /**
   * Last-checked timestamp for the agency view (e.g. when the
   * runner last polled the connection's token). Optional; if not
   * provided the agency view shows an em-dash. Future: thread
   * through from the runner's polling table.
   */
  readonly lastCheckedAt?: string | null;
  /**
   * Invoked when the client clicks "ربط الحساب". For the 110B UI
   * scaffolding this is wired but the underlying button is
   * `disabled` (real OAuth not implemented yet).
   */
  readonly onConnectClick?: (platform: DisplayablePlatform) => void;
  /**
   * Invoked when the client clicks "إعادة الربط" on a
   * reauthorization_required / expired row. Same disabled posture as
   * onConnectClick.
   */
  readonly onReconnectClick?: (platform: DisplayablePlatform) => void;
  /**
   * Invoked when the client confirms the disconnect modal for a
   * connected/reconnect_required/expired row. The card opens its
   * own confirmation modal; the parent only handles the actual API
   * call. The parent should bump its `reloadKey` after success so
   * `list-social-connections` re-fires and the row flips to
   * `disconnected`.
   *
   * Returns a Promise that resolves on backend success / rejects on
   * failure so the card can render per-row loading / error state.
   *
   * Hard rule: the card NEVER calls the disconnect API directly. The
   * parent is the only place that imports `disconnectSocialConnection`
   * from `api/endpoints`. The card is presentational (modal + states);
   * the parent owns transport.
   */
  readonly onDisconnectClick?: (input: {
    readonly connectionId: string;
    readonly platform: DisplayablePlatform;
    readonly reason: string | null;
  }) => Promise<void>;
  /**
   * ID of a connection that's currently being disconnected (so the
   * row can show a spinner and disable the modal's confirm button).
   * Parent owns this state alongside the in-flight tracker for the
   * disconnect API call.
   */
  readonly disconnectInFlightId?: string | null;
}

export function SocialConnectionsCard(props: SocialConnectionsCardProps) {
  const rows = pickActivePerPlatform(props.connections);
  const isClient = props.viewerRole === 'client';

  return (
    <section className="social-connections-card" aria-label="حسابات التواصل الاجتماعي">
      <header className="social-connections-card__header">
        <h3 className="social-connections-card__title">حسابات التواصل الاجتماعي</h3>
        <p className="social-connections-card__subtitle">
          {isClient
            ? 'اربط حساباتك ليتمكن النظام من نشر المحتوى تلقائياً.'
            : 'حالة الربط لكل منصة. يظهر للوكالة فقط للقراءة بدون أي بيانات حسّاسة.'}
        </p>
      </header>
      <ul className="social-connections-card__list">
        {rows.map(({ platform, connection }) => {
          const wireStatus = connection?.status ?? null;
          const status = clientStatusFromWire(wireStatus);
          const accountName = connection?.account_name ?? null;
          const autoHint = canAutoPublishHint(status);
          const showConnectButton = isClient && status === 'not_connected';
          const showReconnectButton =
            isClient && (status === 'reconnect_required' || status === 'expired');
          // Disconnect button shows for any non-archived, NON-disconnected
          // status on a connected (client-side) row. 'reconnect_required'
          // and 'expired' rows can still be cleanly disconnected (e.g.
          // the client wants to abandon the account entirely rather than
          // re-auth). The agency view stays read-only — disconnect is
          // a client-owned action.
          const showDisconnectButton =
            isClient &&
            connection !== null &&
            (status === 'connected' || status === 'reconnect_required' || status === 'expired');

          return (
            <li key={platform} className="social-connections-card__row">
              <div className="social-connections-card__row-meta">
                <div className="social-connections-card__platform">
                  {PLATFORM_LABELS_AR[platform]}
                </div>
                <div className="social-connections-card__platform-desc">
                  {PLATFORM_DESCRIPTIONS_AR[platform]}
                </div>
                {accountName ? (
                  <div className="social-connections-card__account-name">
                    {/* Account display name is a public-facing handle the
                        client themselves shared at OAuth time. NOT a
                        secret. */}
                    الحساب: <span dir="ltr">{accountName}</span>
                  </div>
                ) : null}
              </div>
              <div className="social-connections-card__row-status">
                <span className={statusBadgeTone(status)}>{CLIENT_STATUS_LABELS_AR[status]}</span>
                {!isClient ? (
                  <div
                    className={`social-connections-card__auto-hint social-connections-card__auto-hint--${autoHint.tone}`}
                  >
                    {autoHint.label}
                  </div>
                ) : null}
                {!isClient ? (
                  <div className="social-connections-card__last-checked">
                    آخر فحص:{' '}
                    {props.lastCheckedAt
                      ? new Date(props.lastCheckedAt).toLocaleString('ar-SA')
                      : '—'}
                  </div>
                ) : null}
              </div>
              {showConnectButton || showReconnectButton || showDisconnectButton ? (
                <div className="social-connections-card__row-actions">
                  {showConnectButton ? (
                    <ConnectActionButton
                      kind="connect"
                      platform={platform}
                      onClick={props.onConnectClick}
                    />
                  ) : null}
                  {showReconnectButton ? (
                    <ConnectActionButton
                      kind="reconnect"
                      platform={platform}
                      onClick={props.onReconnectClick}
                    />
                  ) : null}
                  {showDisconnectButton && connection !== null ? (
                    <DisconnectAction
                      connectionId={connection.id}
                      platform={platform}
                      platformLabelAr={PLATFORM_LABELS_AR[platform]}
                      accountName={accountName}
                      onDisconnect={props.onDisconnectClick}
                      isInFlight={props.disconnectInFlightId === connection.id}
                      anyDisconnectInFlight={
                        props.disconnectInFlightId !== null &&
                        props.disconnectInFlightId !== undefined
                      }
                    />
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {isClient ? (
        <p className="social-connections-card__footer-note">
          نستخدم OAuth الرسمي للمنصة. لا نخزن كلمة مرورك ولا نطّلع على أي بيانات حسّاسة. يمكنك فصل
          أي حساب في أي وقت.
        </p>
      ) : (
        <p className="social-connections-card__footer-note">
          الوكالة لا ترى أي رموز وصول أو أسرار — فقط حالة الربط واسم الحساب.
        </p>
      )}
    </section>
  );
}

/**
 * Disconnect surface: "إلغاء الربط" button + confirmation modal +
 * per-row local error state. The parent owns the network call (via
 * `onDisconnect`); this component owns the UI states only:
 *   - 'idle'       → button visible
 *   - 'confirming' → modal visible, awaiting confirm/cancel
 *   - 'submitting' → modal still visible with confirm-disabled +
 *                    spinner copy (parent should also pass
 *                    `disconnectInFlightId` so the spinner appears
 *                    even if parent state lags one tick)
 *   - 'error'      → modal still visible with retry copy and the
 *                    Arabic error message; no raw error.message echo
 *
 * On the parent's success resolution, the parent bumps reloadKey →
 * list-social-connections re-fetches → row's wire status flips →
 * `clientStatusFromWire` flips it to 'disconnected' → the row no
 * longer satisfies `showDisconnectButton` → button + modal disappear
 * cleanly without this component needing a 'success' state.
 */
type DisconnectStage = 'idle' | 'confirming' | 'submitting' | 'error';

function DisconnectAction({
  connectionId,
  platform,
  platformLabelAr,
  accountName,
  onDisconnect,
  isInFlight,
  anyDisconnectInFlight,
}: {
  readonly connectionId: string;
  readonly platform: DisplayablePlatform;
  readonly platformLabelAr: string;
  readonly accountName: string | null;
  readonly onDisconnect: SocialConnectionsCardProps['onDisconnectClick'];
  readonly isInFlight: boolean;
  /**
   * True when SOMEONE — possibly this row, possibly a sibling row —
   * has a disconnect call in flight at the parent. We disable every
   * row's confirm button to prevent the multi-row race where two
   * modals both pass the parent's state-based gate in the same tick.
   * Codex pass-1 P1-2 fix.
   */
  readonly anyDisconnectInFlight: boolean;
}) {
  const [stage, setStage] = useState<DisconnectStage>('idle');
  // Codex pass-1 P1-1 — synchronous re-entrancy guard. React state
  // setters are async (the `disabled` prop doesn't flip until the
  // next render), so two clicks in the same event-loop tick can
  // both pass `submitting === false`. A ref check FIRES synchronously
  // and blocks the second call before it ever reaches `onDisconnect`.
  const submittingRef = useRef(false);
  // Codex pass-1 P2 — cancellation flag so post-await setState calls
  // don't fire on an unmounted component (e.g. user navigated away
  // mid-disconnect). Set in cleanup; checked before each setState.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );
  // We intentionally do NOT thread Supabase's raw error.message into
  // the UI. Generic Arabic copy on every failure branch.
  const wired = typeof onDisconnect === 'function';
  const submitting = stage === 'submitting' || isInFlight;
  // Codex pass-1 P1-2 — disable the confirm button when any row's
  // disconnect is in flight, not just this row's. Prevents the
  // multi-row race where two modals both pass `submitting === false`.
  const confirmDisabled = submitting || !wired || anyDisconnectInFlight;

  async function handleConfirm() {
    // Codex pass-1 P1-1 — synchronous gate (BEFORE any state setter).
    if (submittingRef.current) return;
    submittingRef.current = true;
    if (!wired || !onDisconnect) {
      // Parent didn't wire a handler — should not happen because the
      // card hides the disconnect button when the handler is undefined,
      // but defense-in-depth.
      submittingRef.current = false;
      if (mountedRef.current) setStage('error');
      return;
    }
    if (mountedRef.current) setStage('submitting');
    try {
      await onDisconnect({ connectionId, platform, reason: null });
      // On success: parent bumps reloadKey → list-social-connections
      // re-fetches → row disappears from connected/expired states.
      // Reset local stage so if the parent re-renders us briefly
      // before the refetch lands, we don't show the modal mid-fade.
      if (mountedRef.current) setStage('idle');
    } catch {
      if (mountedRef.current) setStage('error');
    } finally {
      submittingRef.current = false;
    }
  }

  if (stage === 'idle') {
    return (
      <button
        type="button"
        className="btn btn-ghost"
        disabled={!wired || isInFlight}
        title={wired ? undefined : 'تعذّر فصل الحساب الآن — حاول لاحقاً'}
        onClick={() => setStage('confirming')}
      >
        إلغاء الربط
      </button>
    );
  }

  // 'confirming' | 'submitting' | 'error' — all share the modal shell.
  return (
    <div
      className="social-connections-card__modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`disconnect-modal-title-${connectionId}`}
    >
      <div className="social-connections-card__modal">
        <h4
          id={`disconnect-modal-title-${connectionId}`}
          className="social-connections-card__modal-title"
        >
          فصل حساب {platformLabelAr}؟
        </h4>
        <p className="social-connections-card__modal-body">
          سيتم فصل هذا الحساب{accountName ? ' ' : ''}
          {accountName ? <strong dir="ltr">{accountName}</strong> : null} من منصة Super Agency فقط.
          النشر التلقائي إلى هذا الحساب سيتوقف فوراً.
        </p>
        <p className="social-connections-card__modal-note">
          ملاحظة مهمة: هذا الإجراء لا يُلغي إذن التطبيق من إعدادات المنصة الخاصة بك (
          {platform === 'instagram'
            ? 'فيسبوك حيث تتم إدارة حسابات إنستجرام للأعمال'
            : platform === 'tiktok'
              ? 'إعدادات حساب تيك توك للمبدعين'
              : 'إعدادات حساب سناب شات'}
          ). لإلغاء الإذن نهائياً، اذهب إلى إعدادات حسابك في المنصة وأزل تطبيق Super Agency من قائمة
          التطبيقات المرتبطة.
        </p>
        {stage === 'error' ? (
          <div className="banner banner-error" role="alert" style={{ marginBottom: 12 }}>
            تعذّر فصل الحساب. حاول مرة أخرى؛ إذا استمرّت المشكلة فاتصل بفريق الدعم.
          </div>
        ) : null}
        <div className="social-connections-card__modal-actions">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={submitting}
            onClick={() => setStage('idle')}
          >
            إلغاء
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={confirmDisabled}
            onClick={handleConfirm}
          >
            {submitting ? 'جاري الفصل…' : stage === 'error' ? 'إعادة المحاولة' : 'تأكيد الفصل'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Task 113B — single button helper used by both the "ربط الحساب" and
// "إعادة الربط" actions. Centralizing the (kind × status × handler)
// matrix keeps the three-state behavior (live / scaffold / manual)
// from drifting between the two call sites.
function ConnectActionButton({
  kind,
  platform,
  onClick,
}: {
  readonly kind: 'connect' | 'reconnect';
  readonly platform: DisplayablePlatform;
  readonly onClick: ((p: DisplayablePlatform) => void) | undefined;
}) {
  const status = platformOAuthStatus(platform);
  const live = status === 'live' && typeof onClick === 'function';
  const baseLabel = kind === 'connect' ? 'ربط الحساب' : 'إعادة الربط';
  const label = live ? baseLabel : `${baseLabel} (قريباً)`;
  const title = live
    ? undefined
    : status === 'manual'
      ? 'النشر يدوي على هذه المنصة في النسخة الأولى'
      : status === 'scaffold'
        ? 'ميزة الربط التلقائي قيد التحضير لهذه المنصة'
        : 'ميزة الربط التلقائي قيد التحضير';
  return (
    <button
      type="button"
      className="btn"
      disabled={!live}
      title={title}
      onClick={() => {
        if (!live || !onClick) return;
        onClick(platform);
      }}
    >
      {label}
    </button>
  );
}
